import { registerComponent } from "helpers";
import SearchspringComponent from "searchspring";

registerComponent(
  "results-grid",
  class ResultsGrid extends SearchspringComponent {
    siteIDMapping = {
      [WYATT.config.searchspring.siteIds.products]: "products",
      [WYATT.config.searchspring.siteIds.articles]: "articles",
      [WYATT.config.searchspring.siteIds.pages]: "pages",
    };

    constructor() {
      super();

      this.template = this.querySelector('[data-template="item"]');
      this.noResultsTemplate = this.querySelector(
        '[data-template="no-results"]',
      );
      this.gridContentTemplate = this.querySelector(
        '[data-template="grid-content"]',
      );
    }

    get siteId() {
      return this.SearchPage.formData.get("siteId");
    }

    get siteIdKind() {
      return this.siteIDMapping[this.siteId];
    }

    render({ results, pagination }) {
      if (this.impressionObserver) {
        this.impressionObserver.disconnect();
        this.impressionObserver = null;
      }

      this.replaceChildren();

      if (!results || results.length === 0) {
        if (this.noResultsTemplate) {
          const noResults = this.noResultsTemplate.content.cloneNode(true);
          this.appendChild(noResults);
        }
        return;
      }

      this.className = `results-grid results-grid--${this.siteIdKind}`;

      const isProducts = this.siteIdKind === "products";

      // Searchspring's `begin` is the 1-based position of the first result on
      // this page. Without it, row 1 of every page reports position 0 and the
      // top positions look worse than they are.
      const offset =
        pagination && typeof pagination.begin === "number"
          ? pagination.begin - 1
          : 0;

      results.forEach((result, index) => {
        const item = this.template.content.cloneNode(true).firstElementChild;

        switch (this.siteIdKind) {
          case "products":
            item.innerHTML = `
              <product-summary
                data-handle="${result.handle}"
                data-class-list="product-summary--horizontal product-summary--horizontal-until-medium"
              ></product-summary>
            `;
            break;
          case "articles":
            item.innerHTML = `<article-summary data-handle="${result.brand}/${result.uid}"></article-summary>`;
            break;
          case "pages":
            item.innerHTML = `<a href="${result.url}" class="results-grid__item-link">${result.name}</a>`;
            break;
          default:
            item.innerHTML = `<a href="${result.url}" class="results-grid__item-link">${result.name}</a>`;
            break;
        }

        if (isProducts) {
          item.dataset.ssResult = JSON.stringify({
            uid: result.uid,
            parentId: result.parentId ?? result.uid,
            sku: result.sku,
          });
        }

        this.appendChild(item);

        item.addEventListener(
          "click",
          this.handleClick.bind(this, result, offset + index),
        );
      });

      if (this.gridContentTemplate) {
        const gridContent = this.gridContentTemplate.content.cloneNode(true);

        document.importNode(gridContent, true);
      }

      if (isProducts && WYATT.beacon) {
        const surface = WYATT.beacon.detectSurface(this);
        if (surface) {
          this.impressionObserver = WYATT.beacon.observeImpressions(
            this,
            surface,
          );
        }
      }
    }

    handleClick(result, index) {
      const config = WYATT.config.searchspring;

      // Products only: articles and pages have no price or sku, and their
      // `brand` is the blog handle, so they would land in GA4 item reporting
      // as $0 products. Published before the calls below because
      // IntelliSuggest is an unguarded third-party global that only loads as
      // a rollback when Beacon ingestion fails - precisely when this event
      // still needs to fire. Guarded for the mirror of that reason: going
      // first must not let a fault here cost Searchspring its click data.
      if (this.siteIdKind === "products") {
        try {
          this.publish(WYATT.PubSub.events.resultSelected, {
            result,
            index,
            kind: this.siteIdKind,
          });
        } catch (trackingError) {
          console.error(trackingError);
        }
      }

      if (
        config.beaconV2Enabled &&
        this.siteIdKind === "products" &&
        WYATT.beacon
      ) {
        const surface = WYATT.beacon.detectSurface(this);
        if (surface) {
          WYATT.beacon[surface].clickThrough({
            responseId: WYATT.beacon.getResponseId(this),
            results: [WYATT.beacon.normalizeResult(result)],
          });
        }
      }

      if (
        config.legacyTrackingEnabled &&
        typeof IntelliSuggest !== "undefined" &&
        result.intellisuggestData &&
        result.intellisuggestSignature
      ) {
        IntelliSuggest.clickItem(this, {
          siteId: this.siteId,
          intellisuggestData: result.intellisuggestData,
          intellisuggestSignature: result.intellisuggestSignature,
        });
      }
    }
  },
);
